import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Sidebar } from '@/components/shell/Sidebar';
import { Topbar } from '@/components/shell/Topbar';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { clearEntityIndex } from '@/lib/hooks/useEntityIndex';

const COLLAPSE_KEY = 'sidebarCollapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function Layout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [keyName, setKeyName] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const [drawerPath, setDrawerPath] = useState(pathname);
  const drawerRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef(palette);

  useEffect(() => {
    api.me.get().then((i) => setKeyName(i.name)).catch(() => {});
  }, []);

  // Close the drawer on route change (adjust state during render, no effect needed).
  if (drawerPath !== pathname) {
    setDrawerPath(pathname);
    setDrawer(false);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        // Monaco uses Ctrl/Cmd+K as a chord prefix.
        if ((e.target as HTMLElement | null)?.closest?.('.monaco-editor')) return;
        e.preventDefault();
        setPalette((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    paletteRef.current = palette;
  });

  // Drawer: Escape closes, focus moves into the panel and returns to the opener on close.
  useEffect(() => {
    if (!drawer) return;
    const opener = document.activeElement as HTMLElement | null;
    drawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !paletteRef.current && !e.defaultPrevented) setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [drawer]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const logout = () => {
    localStorage.removeItem('apiKey');
    clearEntityIndex();
    navigate('/login');
  };

  return (
    <div className="flex h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow-md focus:ring-2 focus:ring-ring"
      >
        Skip to content
      </a>
      <div className="hidden md:block" inert={drawer}>
        <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} />
      </div>
      {drawer && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground/30" onClick={() => setDrawer(false)} aria-hidden />
          <div ref={drawerRef} role="dialog" aria-modal="true" aria-label="Navigation" tabIndex={-1} className="relative h-full w-64 outline-none">
            <Sidebar collapsed={false} onNavigate={() => setDrawer(false)} className="border-r-0" />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col" inert={drawer}>
        <Topbar onMenu={() => setDrawer(true)} onSearch={() => setPalette(true)} />
        <main id="main" tabIndex={-1} className="flex-1 overflow-auto outline-none">
          <div className="mx-auto w-full max-w-[1280px] space-y-6 p-4 md:p-8">
            <Outlet />
          </div>
        </main>
        <footer className="flex shrink-0 items-center justify-between gap-2 border-t px-4 py-2 text-xs text-muted-foreground md:px-6">
          <span>{keyName ? <>Signed in with <strong className="font-medium text-foreground">{keyName}</strong></> : ' '}</span>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5">
              <LogOut className="size-4" aria-hidden />
              Logout
            </Button>
          </div>
        </footer>
      </div>
      <CommandPalette open={palette} onOpenChange={setPalette} />
    </div>
  );
}
